import { initializeGeometryLoadedTasks, geometryLoadedPrerequisites, initializeRcsGeometryLoadedTasks, rcsGeometryLoadedPrerequisites } from "./GEOMETRY_LOADED/initializeTasks.js";
import { initializeFraglistLoadedTasks } from "./FRAGLIST_LOADED/initializeTasks.js";
import { initializeConsolidationComputedTasks } from "./CONSOLIDATION_COMPUTED/initializeTasks.js";
import { initializeOptimizedOnGpuTasksWebGL, initializeOptimizedOnGpuTasksWebGpu } from "./OPTIMIZED_ON_GPU/initializeTasks.js";
import { USE_WEBGPU, USE_GEOMETRY_OOC } from "../../../globals.js";
import { RCS_NUM_IN_FLIGHT_REQUESTS } from "../../../../file-loaders/rcs/RcsConstants.js";
/** @import {OutOfCoreTileManagerStageConfiguration, Stage} from "../OutOfCoreTileManager.js" */
/** @import {BvhNode} from "../BvhNode.js" */
/** @import {RcsPackage} from "../../../../file-loaders/rcs/RcsPackage.js" */
/**
 * The names of the stages
 * @enum {string}
 */
export const StageNames = {
  INITIAL: "Initial Stage",
  FRAGLIST_LOADED: "Fragment List Loaded",
  GEOMETRY_LOADED: "Geometry Loaded",
  CONSOLIDATION_COMPUTED: "Consolidation Computed",
  OPTIMIZED_ON_GPU: "On GPU (optimized)"
};

/**
 * The type of resource that is used when promoting to the next stage by the tasks in that stage
 * @enum {string}
 */
export const ResourceType = {
  NONE: "None",
  CPU_MEMORY: "Cpu Memory",
  GPU_MEMORY: "Gpu Memory"
};

/** @type {OutOfCoreTileManagerStageConfiguration[]} */
let cachedDefaultStageConfiguration = null;

/** @type {OutOfCoreTileManagerStageConfiguration[]} */
let cachedRcsStageConfiguration = null;

/**
 * Get the default stage configuration for the OutOfCoreTileManager
 * @returns {OutOfCoreTileManagerStageConfiguration[]}
 */
export const getDefaultStageConfiguration = () => {
  if (!cachedDefaultStageConfiguration) {
    cachedDefaultStageConfiguration = [
    {
      stage: StageNames.INITIAL,
      screenSpaceErrorScalingFactor: !USE_WEBGPU ? 1 : 10,
      resource: ResourceType.NONE, // No promotion to this stage, so no resource is needed
      taskInitializationFunctions: []
    },
    {
      stage: StageNames.FRAGLIST_LOADED,
      screenSpaceErrorScalingFactor: !USE_WEBGPU ? 1 : 10,
      inAsyncPromotionLimit: 100,
      minScreenSpaceError: 20,
      resource: ResourceType.NONE,
      taskInitializationFunctions: [
      initializeFraglistLoadedTasks]

    },
    {
      stage: StageNames.GEOMETRY_LOADED,
      screenSpaceErrorScalingFactor: !USE_WEBGPU ? 1 : 10,
      inAsyncPromotionLimit: 50000,
      resource: USE_GEOMETRY_OOC ? ResourceType.CPU_MEMORY : ResourceType.NONE,
      taskInitializationFunctions: [
      initializeGeometryLoadedTasks],

      prerequisites: geometryLoadedPrerequisites
    },
    ...(USE_WEBGPU ?
    [] :

    [{
      stage: StageNames.CONSOLIDATION_COMPUTED,
      screenSpaceErrorScalingFactor: 1,
      inAsyncPromotionLimit: 10,
      resource: ResourceType.NONE,
      taskInitializationFunctions: [
      initializeConsolidationComputedTasks]

    }]),

    {
      stage: StageNames.OPTIMIZED_ON_GPU,
      screenSpaceErrorScalingFactor: 1,
      inAsyncPromotionLimit: !USE_WEBGPU ? 10 : 50000,
      resource: ResourceType.GPU_MEMORY,
      taskInitializationFunctions: [
      USE_WEBGPU ? initializeOptimizedOnGpuTasksWebGpu : initializeOptimizedOnGpuTasksWebGL]

    }];

  }
  return cachedDefaultStageConfiguration;
};

/**
 * Compute screen space error for RCS nodes.
 *
 * @param {BvhNode} node
 * @param {number} screenSpaceError
 * @param {Stage} stage
 * @returns {number}
 */
let computeScreenSpaceErrorRcs = (node, screenSpaceError, stage) => {
  const pkg = /** @type {RcsPackage} */node.model.getData();
  return pkg.getLoadingScreenSpaceError(node.nodeId, screenSpaceError);
};

/**
 * Get the RCS-specific stage configuration for the OutOfCoreTileManager
 * @returns {OutOfCoreTileManagerStageConfiguration[]}
 */
export const getRcsStageConfiguration = () => {
  if (!cachedRcsStageConfiguration) {
    cachedRcsStageConfiguration = [
    {
      stage: StageNames.INITIAL,
      screenSpaceErrorScalingFactor: 1,
      resource: ResourceType.NONE, // No promotion to this stage, so no resource is needed
      taskInitializationFunctions: [],
      computeScreenSpaceError: computeScreenSpaceErrorRcs
    },
    {
      stage: StageNames.GEOMETRY_LOADED,
      screenSpaceErrorScalingFactor: 1,
      inAsyncPromotionLimit: RCS_NUM_IN_FLIGHT_REQUESTS,
      resource: ResourceType.CPU_MEMORY,
      taskInitializationFunctions: [
      initializeRcsGeometryLoadedTasks // Use RCS-specific tasks
      ],
      prerequisites: rcsGeometryLoadedPrerequisites, // Use RCS-specific prerequisites
      computeScreenSpaceError: computeScreenSpaceErrorRcs
    },
    ...(USE_WEBGPU ?
    [] :

    [{
      stage: StageNames.CONSOLIDATION_COMPUTED,
      screenSpaceErrorScalingFactor: 1,
      inAsyncPromotionLimit: 1,
      resource: ResourceType.NONE,
      taskInitializationFunctions: [
      initializeConsolidationComputedTasks],

      computeScreenSpaceError: computeScreenSpaceErrorRcs
    }]),

    {
      stage: StageNames.OPTIMIZED_ON_GPU,
      screenSpaceErrorScalingFactor: 1,
      inAsyncPromotionLimit: !USE_WEBGPU ? 10 : 50000,
      resource: ResourceType.GPU_MEMORY,
      taskInitializationFunctions: [
      USE_WEBGPU ? initializeOptimizedOnGpuTasksWebGpu : initializeOptimizedOnGpuTasksWebGL],

      computeScreenSpaceError: computeScreenSpaceErrorRcs
    }];

  }
  return cachedRcsStageConfiguration;
};